import { useCallback, useEffect, useState } from 'react'
import type { Action, ActionDraft, ActiveAction } from '../../domain/action'
import type { Area, AreaDraft } from '../../domain/area'
import { levelFor } from '../../domain/area'
import type { LeveleriaRepository } from '../../domain/leveleriaRepository'
import type { Specialization } from '../../domain/specialization'
import type { Objective, ObjectiveDraft, CheckpointAction } from '../../domain/objective'
import type { ActivityRecord, Milestone } from '../../domain/history'
import type { RewardItem, RewardRedemption } from '../../domain/reward'
import type { LeveleriaBackup } from '../../domain/backup'
import { useI18n } from '../../i18n'

export interface LevelUpEvent {
  areaName: string
  areaColor: string
  oldLevel: number
  newLevel: number
}

export const useLeveleria = (repository: LeveleriaRepository) => {
  const { language, copy: { common } } = useI18n()
  const errorText = (cause: unknown, fallback: string) => language === 'es' && cause instanceof Error ? cause.message : fallback
  const [areas, setAreas] = useState<Area[]>([])
  const [actions, setActions] = useState<Action[]>([])
  const [specializations, setSpecializations] = useState<Specialization[]>([])
  const [objectives, setObjectives] = useState<Objective[]>([])
  const [activityRecords, setActivityRecords] = useState<ActivityRecord[]>([])
  const [milestones, setMilestones] = useState<Milestone[]>([])
  const [rewards, setRewards] = useState<RewardItem[]>([])
  const [rewardRedemptions, setRewardRedemptions] = useState<RewardRedemption[]>([])
  const [targetRewardId, setTargetRewardIdState] = useState<string | null>(null)
  const [activeAction, setActiveAction] = useState<ActiveAction | null>(null)
  const [coins, setCoins] = useState(0)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    try {
      setError(null)
      const [loadedAreas, loadedActions, loadedSpecializations, loadedObjectives, loadedActivityRecords, loadedMilestones, loadedRewards, loadedRedemptions, loadedTargetRewardId, loadedActiveAction, loadedCoins] = await Promise.all([
        repository.list(),
        repository.listActions(undefined, true),
        repository.listSpecializations(),
        repository.listObjectives(),
        repository.listActivityRecords(),
        repository.listMilestones(),
        repository.listRewards(),
        repository.listRewardRedemptions(),
        repository.getTargetRewardId(),
        repository.getActiveAction(),
        repository.getCoins(),
      ])
      const activeStillExists = loadedActiveAction && loadedActions.some((action) => action.id === loadedActiveAction.actionId && action.areaId === loadedActiveAction.areaId && action.completedAt === null)
      setAreas(loadedAreas)
      setActions(loadedActions)
      setSpecializations(loadedSpecializations)
      setObjectives(loadedObjectives)
      setActivityRecords(loadedActivityRecords)
      setMilestones(loadedMilestones)
      setRewards(loadedRewards)
      setRewardRedemptions(loadedRedemptions)
      setTargetRewardIdState(loadedTargetRewardId)
      setActiveAction(activeStillExists ? loadedActiveAction : null)
      setCoins(loadedCoins)
      if (loadedActiveAction && !activeStillExists) await repository.saveActiveAction(null)
    } catch (cause) {
      setError(errorText(cause, common.loadFailed))
    } finally {
      setLoading(false)
    }
  }, [repository, language, common.loadFailed])

  useEffect(() => { void load() }, [load])

  const run = async (operation: () => Promise<void>) => {
    setSaving(true)
    setError(null)
    try {
      await operation()
      return true
    } catch (cause) {
      setError(errorText(cause, common.saveFailed))
      return false
    } finally {
      setSaving(false)
    }
  }

  const createArea = async (draft: AreaDraft): Promise<Area | false> => {
    setSaving(true)
    setError(null)
    try {
      const created = await repository.create(draft)
      setAreas((current) => [...current, created])
      return created
    } catch (cause) {
      setError(errorText(cause, common.saveFailed))
      return false
    } finally {
      setSaving(false)
    }
  }

  const updateArea = (id: string, draft: AreaDraft) => run(async () => {
    const updated = await repository.update(id, draft)
    setAreas((current) => current.map((area) => area.id === id ? updated : area))
  })

  const deleteArea = (id: string) => run(async () => {
    await repository.delete(id)
    setAreas((current) => current.filter((area) => area.id !== id))
    setActions((current) => current.filter((action) => action.areaId !== id))
    setSpecializations((current) => current.filter((item) => item.areaId !== id))
    setObjectives((current) => current.filter((item) => item.areaId !== id))
    setActiveAction((current) => current?.areaId === id ? null : current)
  })

  const createAction = (areaId: string, draft: ActionDraft) => run(async () => {
    const created = await repository.createAction(areaId, draft)
    setActions((current) => [...current, created])
    if (draft.newSpecializationName) setSpecializations((await repository.listSpecializations()))
  })

  const updateAction = (id: string, draft: ActionDraft) => run(async () => {
    const updated = await repository.updateAction(id, draft)
    setActions((current) => current.map((action) => action.id === id ? updated : action))
  })

  const deleteAction = (id: string) => run(async () => {
    await repository.deleteAction(id)
    setActions((current) => current.filter((action) => action.id !== id))
    setActiveAction((current) => current?.actionId === id ? null : current)
  })

  const createSpecialization = (areaId: string, name: string) => run(async () => {
    const created = await repository.createSpecialization(areaId, name)
    setSpecializations((current) => [...current, created])
  })

  const deleteSpecialization = (id: string) => run(async () => {
    const updatedActions = await repository.deleteSpecialization(id)
    const byId = new Map(updatedActions.map((action) => [action.id, action]))
    setSpecializations((current) => current.filter((item) => item.id !== id))
    setActions((current) => current.map((action) => byId.get(action.id) ?? action))
  })

  const refreshObjectiveData = async (includeActions = false) => {
    const [loadedAreas, loadedObjectives, loadedActions] = await Promise.all([
      repository.list(),
      repository.listObjectives(),
      includeActions ? repository.listActions(undefined, true) : Promise.resolve<Action[] | null>(null),
    ])
    setAreas(loadedAreas)
    setObjectives(loadedObjectives)
    if (loadedActions) setActions(loadedActions)
  }

  const createObjective = (areaId: string, draft: ObjectiveDraft) => run(async () => {
    await repository.createObjective(areaId, draft)
    await refreshObjectiveData()
  })

  const updateObjective = (id: string, draft: ObjectiveDraft) => run(async () => {
    await repository.updateObjective(id, draft)
    await refreshObjectiveData()
  })

  const addMissionNote = (id: string, text: string, nextStep: string, action?: CheckpointAction) => run(async () => {
    await repository.addMissionNote(id, text, nextStep, action)
    await refreshObjectiveData(true)
  })

  const activateObjective = (id: string) => run(async () => {
    await repository.activateObjective(id)
    await refreshObjectiveData()
  })

  const completeObjective = (id: string) => run(async () => {
    await repository.completeObjective(id)
    await Promise.all([
      refreshObjectiveData(true),
      repository.listMilestones().then(setMilestones),
    ])
  })

  const closeObjective = (id: string) => run(async () => {
    await repository.closeObjective(id)
    await refreshObjectiveData(true)
  })

  const repeatObjective = (id: string) => run(async () => {
    await repository.repeatObjective(id)
    await refreshObjectiveData(true)
  })

  const moveObjective = (id: string, delta: -1 | 1) => run(async () => {
    await repository.moveObjective(id, delta)
    setObjectives(await repository.listObjectives())
  })

  const toggleActionObjective = (objectiveId: string, actionId: string) => run(async () => {
    await repository.toggleActionObjective(objectiveId, actionId)
    setActions(await repository.listActions(undefined, true))
  })

  const activateAction = (action: Action) => run(async () => {
    const active: ActiveAction = { actionId: action.id, areaId: action.areaId, amount: 1, startedAt: Date.now() }
    await repository.saveActiveAction(active)
    setActiveAction(active)
  })

  const setActiveAmount = (amount: number) => {
    setActiveAction((current) => {
      if (!current) return null
      const updated = { ...current, amount: Math.min(999_999_999, Math.max(1, Math.trunc(amount))) }
      void repository.saveActiveAction(updated).catch((cause) => {
        setError(errorText(cause, common.amountFailed))
      })
      return updated
    })
  }

  const abandonActiveAction = () => run(async () => {
    await repository.saveActiveAction(null)
    setActiveAction(null)
  })

  const createReward = (name: string, cost: number) => run(async () => {
    const created = await repository.createReward(name, cost)
    setRewards((current) => [...current, created])
  })

  const deleteReward = (id: string) => run(async () => {
    await repository.deleteReward(id)
    setRewards((current) => current.filter((reward) => reward.id !== id))
    setTargetRewardIdState((current) => current === id ? null : current)
  })

  const setTargetReward = (id: string | null) => run(async () => {
    await repository.setTargetRewardId(id)
    setTargetRewardIdState(id)
  })

  const redeemReward = (id: string) => run(async () => {
    const result = await repository.redeemReward(id)
    setRewards((current) => current.map((reward) => reward.id === id ? result.reward : reward))
    setRewardRedemptions((current) => [result.redemption, ...current])
    setCoins(result.coins)
  })

  const exportBackup = async (): Promise<LeveleriaBackup | null> => {
    setSaving(true)
    setError(null)
    try {
      return await repository.exportBackup()
    } catch (cause) {
      setError(errorText(cause, common.backupFailed))
      return null
    } finally {
      setSaving(false)
    }
  }

  const importBackup = (backup: LeveleriaBackup) => run(async () => {
    await repository.importBackup(backup)
    await load()
  })

  const completeActiveAction = async (): Promise<LevelUpEvent | null | false> => {
    if (!activeAction) return false
    let levelUp: LevelUpEvent | null = null
    const succeeded = await run(async () => {
      const previousArea = areas.find((area) => area.id === activeAction.areaId)
      if (!previousArea) throw new Error(common.areaMissing)
      const result = await repository.completeAction(activeAction)
      const oldLevel = levelFor(previousArea.xp)
      const newLevel = levelFor(result.area.xp)
      setAreas((current) => current.map((area) => area.id === result.area.id ? result.area : area))
      setActions((current) => current.map((action) => action.id === result.action.id ? result.action : action))
      setCoins(result.coins)
      setSpecializations((current) => current.map((item) => result.specializations.find((updated) => updated.id === item.id) ?? item))
      setActiveAction(null)
      const [records, achievedMilestones] = await Promise.all([
        repository.listActivityRecords(),
        repository.listMilestones(),
      ])
      setActivityRecords(records)
      setMilestones(achievedMilestones)
      if (newLevel > oldLevel) {
        levelUp = { areaName: result.area.name, areaColor: result.area.color, oldLevel, newLevel }
      }
    })
    return succeeded ? levelUp : false
  }

  return {
    areas,
    actions: actions.filter((action) => action.completedAt === null),
    missionActions: actions,
    specializations,
    objectives,
    activityRecords,
    milestones,
    rewards,
    rewardRedemptions,
    targetRewardId,
    activeAction,
    coins,
    loading,
    saving,
    error,
    clearError: () => setError(null),
    createArea,
    updateArea,
    deleteArea,
    createAction,
    updateAction,
    deleteAction,
    createSpecialization,
    deleteSpecialization,
    createObjective,
    updateObjective,
    addMissionNote,
    activateObjective,
    completeObjective,
    closeObjective,
    repeatObjective,
    moveObjective,
    toggleActionObjective,
    activateAction,
    setActiveAmount,
    abandonActiveAction,
    createReward,
    deleteReward,
    setTargetReward,
    redeemReward,
    exportBackup,
    importBackup,
    completeActiveAction,
  }
}
